import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { CssBaseline, IconButton, Tooltip } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { ThemeProvider as EmotionTheme } from '@emotion/react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en-gb';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { usajiliTheme, usajiliLightTheme } from './theme';

export type USAJILIColorMode = 'dark' | 'light';

const STORAGE_KEY = 'usajili_color_mode';

/** Header/app-bar colours that sit outside the MUI palette. */
export const USAJILI_CHROME = {
  dark: {
    appbarBg: '#17120c',
    appbarBorder: '#2a2317',
    textDim: '#8b95a5',
    textMain: '#e8e3dc',
    contentBg: '#0f1419',
  },
  light: {
    appbarBg: '#FFFFFF',
    appbarBorder: '#D5DAE1',
    textDim: '#4B5563',
    textMain: '#111827',
    contentBg: '#F3F5F8',
  },
} as const;

interface ColorModeCtx {
  mode: USAJILIColorMode;
  toggle: () => void;
  chrome: (typeof USAJILI_CHROME)[USAJILIColorMode];
}

const Ctx = createContext<ColorModeCtx | null>(null);

export const useUSAJILIColorMode = (): ColorModeCtx => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useUSAJILIColorMode must be used inside USAJILIThemeProvider');
  return v;
};

function readStoredMode(): USAJILIColorMode {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

/** Light/dark theme for the USAJILI pages; the choice persists per browser. */
export const USAJILIThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<USAJILIColorMode>(readStoredMode);

  const toggle = useCallback(() => {
    setMode((m) => {
      const next = m === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* storage unavailable — still switch for this session */
      }
      return next;
    });
  }, []);

  const muiTheme = mode === 'light' ? usajiliLightTheme : usajiliTheme;
  const value = useMemo(() => ({ mode, toggle, chrome: USAJILI_CHROME[mode] }), [mode, toggle]);

  return (
    <Ctx.Provider value={value}>
      <ThemeProvider theme={muiTheme}>
        <EmotionTheme theme={muiTheme as any}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <CssBaseline />
            {children}
          </LocalizationProvider>
        </EmotionTheme>
      </ThemeProvider>
    </Ctx.Provider>
  );
};

/** Sun/moon button that switches between light and dark mode. */
export const USAJILIColorModeToggle: React.FC<{ color?: string }> = ({ color }) => {
  const { mode, toggle } = useUSAJILIColorMode();
  const label = mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';
  return (
    <Tooltip title={label}>
      <IconButton size="small" onClick={toggle} aria-label={label} sx={{ color: color || 'text.secondary' }}>
        {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
};
